/**
 * Tournage, « **La prise parfaite** » (⟦C53 · A-170 §2⟧, remplace « La bonne prise » ⟦A-3⟧).
 *
 * Matt, 01/10/2026 : *« je ne comprends pas pourquoi le rond et la grille des tiers ne sont pas
 * calés […] c'est trop horrible. On ne peut pas inventer un autre jeu ? »* Parmi trois propositions
 * il a choisi celle-ci : une jauge de jeu d'acteur. Un curseur va et vient, une zone verte marque le
 * moment juste, on appuie sur **REC** (Espace, clic ou toucher) quand il est dedans. Cinq prises par
 * manche ; à chaque prise la zone rétrécit et le curseur accélère.
 *
 * ## Qui décide de quoi
 *
 * - **Le serveur** tire les paramètres de chaque prise (vitesse, zone, variante : une zone qui
 *   glisse, un curseur qui change de vitesse, une zone dorée, l'acteur qui éternue) et **note**.
 * - **Le client** ne fait que jouer : il dessine la jauge, mesure l'instant de chaque appui, et
 *   n'envoie QUE ça (`{ appuis: [{ prise, atMs }] }`). Jamais un score, jamais une position de curseur.
 * - **La fonction du curseur est la même des deux côtés** (`shared/src/atelier/prise-parfaite.ts`) :
 *   ce que le joueur voit est ce que le serveur rejoue.
 *
 * ## Le temps d'une prise
 *
 * `t = 0` à l'instant où le curseur commence à bouger (la fin du clap). L'appui est mesuré par
 * l'horodatage de l'événement (`event.timeStamp`), pas par le dernier rendu : la précision de
 * l'appui ne dépend pas du taux de rafraîchissement de l'écran. Quand l'onglet est caché
 * (`requestAnimationFrame` s'arrête, pas l'horloge), le temps de jeu se fige au lieu de sauter.
 *
 * ⟦A-56⟧ *Le plan lumière* n'est plus une phase : *Le Setup* élargit la zone verte, point.
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte.** Consigne : une phrase, la touche, un badge de variante. PENDANT
 * la prise : la jauge, un mot de règle (« Suis la zone. », « Il éternue ? Attends. ») et, au verdict, UN
 * mot dans la bulle (« En or ! », « Parfaite ! », « Raté », « Trop tard ») : jamais une phrase qui oblige à
 * lâcher la jauge des yeux. Les phrases d'avant (la règle entière, ce que fait chaque variante, le détail
 * d'un verdict) restent : derrière « Les dessous » de la consigne, et lues par le lecteur d'écran.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  APPUI_MIN_MS,
  PRISES_PAR_MANCHE,
  centreZoneA,
  etatEternuement,
  evaluerAppui,
  positionCurseur,
  type NoteManche,
  type ResultatPrise,
} from "@maxime/shared";

import type { TimelineTournage } from "../../api/atelier";
import { DessousBloc, DessousLignes } from "../../components/LesDessous";
import { useSfx } from "../../components/SoundProvider";
import { zoneSM } from "../../game/format";
import { spriteHeros } from "../../game/heros";
import { useUIStore } from "../../stores/uiStore";
import { BlocChiffresDuTour } from "../core/BlocChiffresDuTour";
import { DessousMiniJeu } from "../core/DessousMiniJeu";
import { ligneExigence } from "../core/exigence";
import { NoteSur100 } from "../core/NoteSur100";
import { COMMANDES, CONSIGNE, MOTS_VERDICT, ligneBilanTournage } from "../core/textes";
import { SEUIL_PAUSE_MS } from "../core/useRafLoop";
import { useTremblementSM } from "../core/useTremblementSM";
import type { ProprietesMiniJeu } from "../core/types";
import { Camera, Clap, Projecteur } from "./Decor";
import {
  CONSIGNES,
  VERDICTS,
  depuisServeur,
  genererPriseParfaite,
  noterPriseParfaite,
} from "./pattern";

type Phase = "pret" | "clap" | "tourne" | "verdict" | "fini";

/** Le clap dure ce qu'il faut pour le lire et l'entendre ; plus court quand les animations sont coupées. */
const CLAP_MS = 1000;
const CLAP_POSE_MS = 600;
/** Le temps de lire le verdict avant la prise suivante. */
const VERDICT_MS = 1500;

/**
 * L'horodatage d'un événement, sur la même échelle que `performance.now()`. `timeStamp` est la
 * date à laquelle l'entrée est arrivée (plus juste que « maintenant » dans le gestionnaire) ; un
 * vieux navigateur qui le donnerait en millisecondes d'epoch retombe sur l'horloge de la page.
 */
function horodatage(e: { timeStamp: number }): number {
  return e.timeStamp > 1e11 || e.timeStamp <= 0 ? performance.now() : e.timeStamp;
}

export function PriseParfaite({
  stage,
  state,
  seed,
  pattern: servi,
  onFini,
  accueil,
  actionsPlus,
  consequence,
}: ProprietesMiniJeu) {
  /** ⟦A-76⟧ SPEC-UI §7.5 : en zone rouge de SM, la zone de jeu tremble (±1 px / 280 ms). */
  const tremblement = useTremblementSM(state);
  /** ⟦A-83⟧ `prefers-reduced-motion` : la variante posée (SPEC-UI §7.4), jumelle du serveur. */
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const sfx = useSfx();

  const pattern = useMemo(
    () =>
      servi
        ? depuisServeur(servi.patterns.tournage, state)
        : genererPriseParfaite({ state, seed, pose: reducedMotion }),
    [servi, state, seed, reducedMotion],
  );
  const prises = pattern.prises;

  const [phase, setPhase] = useState<Phase>("pret");
  const [rang, setRang] = useState(0);
  /** Millisecondes depuis le départ du curseur de la prise courante. */
  const [t, setT] = useState(0);
  const [resultats, setResultats] = useState<ResultatPrise[]>([]);
  const [fin, setFin] = useState<{ note: NoteManche; timeline: TimelineTournage } | null>(null);

  // Des refs pour tout ce que les gestionnaires d'événements lisent : ils vivent plus longtemps
  // qu'un rendu, et un double appui ne doit jamais voir une phase périmée.
  const phaseRef = useRef<Phase>("pret");
  phaseRef.current = phase;
  const rangRef = useRef(0);
  rangRef.current = rang;
  const t0 = useRef(0);
  const appuis = useRef<TimelineTournage["appuis"]>([]);

  const prise = prises[Math.min(rang, prises.length - 1)]!;
  /** Ce que le héros joue : sa planche selon le genre et la santé (⟦C15⟧), ou le sprite R3 faute de genre. */
  const sprite = spriteHeros("idle", state.genre, zoneSM(state.mental));

  // ── Le déroulé d'une prise : clap → curseur → verdict ─────────────────────────────────────

  const lancerClap = useCallback(
    (r: number) => {
      rangRef.current = r;
      setRang(r);
      setT(0);
      setPhase("clap");
      sfx("cut_hit");
    },
    [sfx],
  );

  const commencer = useCallback(() => {
    appuis.current = [];
    setResultats([]);
    setFin(null);
    lancerClap(0);
  }, [lancerClap]);

  /** Le clap a claqué : le curseur part. */
  useEffect(() => {
    if (phase !== "clap") return;
    const id = window.setTimeout(() => setPhase("tourne"), reducedMotion ? CLAP_POSE_MS : CLAP_MS);
    return () => window.clearTimeout(id);
  }, [phase, rang, reducedMotion]);

  /** La prise est finie, un appui (`atMs`) ou le bout du curseur (`null`). Une seule fois par prise. */
  const terminer = useCallback(
    (atMs: number | null) => {
      if (phaseRef.current !== "tourne") return;
      phaseRef.current = "verdict"; // avant le rendu : un second appui, dans la même frame, ne compte pas
      const r = rangRef.current;
      const p = prises[r]!;
      const resultat = evaluerAppui(p, atMs);
      if (atMs !== null) appuis.current.push({ prise: r, atMs });
      setT(atMs ?? p.dureeMs);
      setResultats((liste) => [...liste, resultat]);
      setPhase("verdict");
      // Le son dit le verdict : un cri de réussite, une réussite en or, ou le son doux du raté.
      sfx(resultat.issue === "doree" ? "cut_perfect" : resultat.points > 0 ? "take_hit" : "take_miss");
    },
    [prises, sfx],
  );

  /** Le curseur court. `t0` est l'instant du départ ; l'horloge se fige quand la page est cachée. */
  useEffect(() => {
    if (phase !== "tourne") return;
    const duree = prises[rangRef.current]!.dureeMs;
    t0.current = performance.now();
    let precedent = t0.current;
    let brut = 0;
    const frame = (maintenant: number) => {
      const delta = maintenant - precedent;
      precedent = maintenant;
      // Onglet caché, machine en veille : `requestAnimationFrame` s'arrête, `performance.now()`
      // non. On ne laisse pas le curseur sauter d'une jauge entière au retour.
      if (delta >= SEUIL_PAUSE_MS) t0.current += delta;
      const ecoule = Math.max(0, maintenant - t0.current);
      if (ecoule >= duree) {
        terminer(null);
        return;
      }
      setT(ecoule);
      brut = requestAnimationFrame(frame);
    };
    setT(0);
    brut = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(brut);
  }, [phase, rang, prises, terminer]);

  /** Un appui sur REC, clavier, souris ou doigt. Avant `APPUI_MIN_MS`, personne n'a pu réagir : on l'ignore. */
  const appuyer = useCallback(
    (instant: number) => {
      if (phaseRef.current !== "tourne") return;
      const atMs = Math.round(instant - t0.current);
      // Le serveur voit un appui plus précoce comme une trace fabriquée (`appui_trop_precoce`) :
      // un joueur honnête qui martèle REC à la fin du clap n'est donc jamais neutralisé à tort.
      if (atMs < APPUI_MIN_MS) return;
      terminer(atMs);
    },
    [terminer],
  );

  useEffect(() => {
    if (phase !== "tourne") return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.repeat || (e.code !== "Space" && e.key !== "Enter")) return;
      e.preventDefault();
      appuyer(horodatage(e));
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [phase, appuyer]);

  /** Le verdict a été lu : la prise suivante, ou la fin de la manche. */
  useEffect(() => {
    if (phase !== "verdict") return;
    const id = window.setTimeout(() => {
      if (rangRef.current + 1 < prises.length) {
        lancerClap(rangRef.current + 1);
        return;
      }
      // Copie : la timeline est un instantané de ce qui a été joué, pas une vue sur le tampon.
      const timeline: TimelineTournage = { appuis: [...appuis.current] };
      setFin({ note: noterPriseParfaite(pattern, timeline), timeline });
      setPhase("fini");
    }, VERDICT_MS);
    return () => window.clearTimeout(id);
  }, [phase, prises, pattern, lancerClap]);

  // ── Ce que la scène montre ──────────────────────────────────────────────────────────────

  const enPrise = phase === "tourne";
  /** Le curseur et la zone à l'instant affiché : celui de la prise, ou celui de l'appui (verdict). */
  const instant = phase === "clap" ? 0 : t;
  const curseur = positionCurseur(prise, instant);
  const centre = centreZoneA(prise, instant);
  const demi = prise.zone.demi;
  const eternue = phase === "tourne" || phase === "verdict" ? etatEternuement(prise, instant) : "aucun";
  const consigne = CONSIGNES[prise.variante];
  const issueVerdict = phase === "verdict" ? (resultats[rang]?.issue ?? "sans_appui") : null;
  const verdict = issueVerdict ? VERDICTS[issueVerdict] : null;
  const dansLeVert = Math.abs(curseur - centre) <= demi;

  // Le snif de l'acteur s'entend : c'est l'annonce de l'éternuement.
  useEffect(() => {
    if (eternue === "snif") sfx("event_notif");
  }, [eternue, sfx]);

  /** La posture du héros, en une classe : il renifle, il éternue, il se réjouit, il se tasse. */
  const posture =
    eternue === "snif"
      ? "snif"
      : eternue === "atchoum"
        ? "atchoum"
        : verdict
          ? verdict.reussi
            ? "bravo"
            : "rate"
          : enPrise
            ? "joue"
            : "";

  // ───────────────────────────── Prêt ─────────────────────────────

  if (phase === "pret") {
    return (
      <div className="jeu jeu-prise">
        <h3 className="jeu-titre">La prise parfaite</h3>
        {pattern.pose ? (
          <p className="jeu-variante" role="note">
            {CONSIGNE.tournage.pose}
          </p>
        ) : null}
        {/* ⟦C62 · friction 3⟧ La 1re fois, la réplique de Matt tient la place de la phrase de règle : un seul écran. */}
        {accueil ?? <p className="jeu-regle">{CONSIGNE.tournage.regle}</p>}
        <p className="jeu-regle jeu-commandes">
          <strong>REC</strong> : {COMMANDES.tournage}
        </p>
        <ul className="jeu-params">
          <li>
            <strong>{PRISES_PAR_MANCHE}</strong> prises
          </li>
          {/* ⟦C62 · friction 7⟧ « exigence E0 » est derrière « Les dessous » (« Les chiffres du tour »). */}
          {pattern.premiereAssuree > 0 ? (
            <li>première prise assurée</li>
          ) : null}
          {pattern.pardons > 0 ? (
            <li>
              <strong>{pattern.pardons}</strong> prise{pattern.pardons > 1 ? "s" : ""} ratée
              {pattern.pardons > 1 ? "s" : ""} effacée{pattern.pardons > 1 ? "s" : ""}
            </li>
          ) : null}
        </ul>
        <div className="jeu-actions">
          {/* ⟦C62 · friction 3⟧ Un seul libellé pour lancer un mini-jeu : « Jouer ». */}
          <button className="bouton primaire grand" type="button" onClick={commencer}>
            Jouer
          </button>
          {actionsPlus}
          {/* ⟦C57 · lot 8⟧ La règle d'avant, au mot près, puis ce que fait chaque variante, puis la leçon de Matt. */}
          <DessousMiniJeu stage={stage}>
            <BlocChiffresDuTour lignes={[ligneExigence(pattern.k)]} />
            <DessousBloc titre="La règle">
              <p className="dessous-texte">
                {pattern.pose
                  ? "Mode posé : le curseur est ralenti et rien ne vient te surprendre. "
                  : ""}
                Un curseur va et vient sur la jauge. Appuie sur <strong>REC</strong> (Espace, clic ou
                toucher) quand il est dans le vert : plus tu es près du centre, mieux c'est. Cinq prises,
                et à chaque prise la zone rétrécit et le curseur accélère.
              </p>
            </DessousBloc>
            <DessousBloc titre="Les variantes">
              <DessousLignes
                lignes={Object.values(CONSIGNES).flatMap((c) =>
                  c
                    ? [
                        {
                          icone: "🎬",
                          // « La zone glisse : La zone glisse : … » : le badge n'est répété que s'il apporte un mot.
                          texte: c.consigne.startsWith(c.badge) ? c.consigne : `${c.badge} : ${c.consigne}`,
                        },
                      ]
                    : [],
                )}
              />
            </DessousBloc>
          </DessousMiniJeu>
        </div>
      </div>
    );
  }

  // ───────────────────────────── Fini ─────────────────────────────

  if (phase === "fini" && fin) {
    const { note, timeline } = fin;
    const bonnes = note.prises.filter((p) => p.points > 0).length;
    const ors = note.prises.filter((p) => p.issue === "doree").length;
    return (
      <div className="jeu jeu-prise">
        <div className="prise-fin-tete">
          <h3 className="jeu-titre">Coupez ! C'est dans la boîte.</h3>
          <NoteSur100 note={note.score} />
        </div>
        <ol className="prise-bilan" aria-label="Le bilan des cinq prises">
          {note.prises.map((p, i) => {
            const v = VERDICTS[p.issue];
            const effacee = note.pardonnees.includes(i);
            return (
              <li key={i} className={`${v.reussi ? "ok" : "ko"}${effacee ? " effacee" : ""}`}>
                <span className="prise-bilan-rang">Prise {i + 1}</span>
                <span className="prise-bilan-texte">
                  {v.court}
                  {effacee ? " (effacée)" : ""}
                </span>
                <strong className="prise-bilan-points">{p.points}</strong>
              </li>
            );
          })}
        </ol>
        <p className="jeu-seuils">{ligneBilanTournage(bonnes, note.prises.length, ors)}</p>
        {consequence?.(note.score) ? <p className="jeu-seuils">{consequence(note.score)}</p> : null}
        <button
          className="bouton primaire grand"
          type="button"
          onClick={() => onFini(note.score, timeline)}
        >
          Suivant
        </button>
      </div>
    );
  }

  // ───────────────────────────── La prise ─────────────────────────────

  return (
    <div className="jeu jeu-prise" style={tremblement}>
      <div className="prise-entete">
        <span className="prise-compteur">
          Prise {rang + 1} / {prises.length}
        </span>
        <ol className="prise-pastilles" aria-label="Les cinq prises">
          {prises.map((_, i) => {
            const r = resultats[i];
            const etat = r ? (r.points > 0 ? (r.issue === "doree" ? "or" : "ok") : "ko") : i === rang ? "courante" : "";
            return <li key={i} className={`prise-pastille ${etat}`} />;
          })}
        </ol>
      </div>

      <div className="prise-plateau" onPointerDown={(e) => e.button === 0 && appuyer(horodatage(e))}>
        <div className={`prise-scene ${posture}`}>
          <span className="prise-mur" aria-hidden="true" />
          <span className="prise-sol" aria-hidden="true" />
          <span className="prise-projecteur prise-projecteur-g" aria-hidden="true">
            <Projecteur />
          </span>
          <span className="prise-projecteur prise-projecteur-d" aria-hidden="true">
            <Projecteur />
          </span>
          <span className={`prise-voyant-rec ${enPrise ? "allume" : ""}`} aria-hidden="true">
            REC
          </span>
          {/* Le nom de la variante s'efface quand une bulle prend la place (verdict, snif) : ils se chevaucheraient. */}
          {consigne && !verdict && eternue === "aucun" ? <span className="prise-badge">{consigne.badge}</span> : null}
          <span className="prise-camera" aria-hidden="true">
            <Camera voyant={enPrise} />
          </span>

          <span className="prise-ombre" aria-hidden="true" />
          <img className="prise-acteur" src={sprite} alt="" draggable={false} />

          {phase === "clap" ? (
            <span className={`prise-clap ${reducedMotion ? "pose" : ""}`} aria-hidden="true">
              <Clap />
              <span className="prise-clap-texte">
                <span className="prise-clap-titre">PRISE</span>
                <span className="prise-clap-numero">{rang + 1}</span>
              </span>
            </span>
          ) : null}

          {verdict ? (
            <span className={`prise-bulle ${verdict.reussi ? "ok" : "ko"}`} role="status">
              {/* ⟦C57 · lot 8⟧ UN mot à l'écran ; la réplique et son détail restent pour le lecteur d'écran. */}
              <strong>{MOTS_VERDICT[issueVerdict ?? "sans_appui"]}</strong>
              <small className="visuellement-cache">
                {verdict.texte} : {verdict.detail}
              </small>
            </span>
          ) : null}
          {!verdict && (eternue === "snif" || eternue === "atchoum") ? (
            <span className="prise-bulle eternue" role="status">
              <strong>{eternue === "snif" ? "Snif…" : "Atchoum !"}</strong>
            </span>
          ) : null}
        </div>

        <div className="prise-commandes">
          <div
            className={`prise-jauge ${eternue !== "aucun" ? "eternue" : ""} ${verdict ? (verdict.reussi ? "ok" : "ko") : ""}`}
          >
            {/* La règle de la prise (sa variante) ou le rappel du geste : une ligne, toujours la même place. */}
            {/* ⟦C57 · lot 8⟧ Le mot à l'écran ; la phrase d'origine reste pour le lecteur d'écran. */}
            <p className="prise-regle" role={consigne ? "note" : undefined} aria-label={consigne?.consigne}>
              {consigne ? consigne.court : "Dans le vert !"}
            </p>
            <div className="prise-piste">
              <span
                className={`prise-zone ${enPrise && dansLeVert ? "dedans" : ""}`}
                style={{ left: `${(centre - demi) * 100}%`, width: `${demi * 200}%` }}
              >
                {prise.or ? (
                  <span className="prise-or" style={{ width: `${(prise.or.demi / demi) * 100}%` }} />
                ) : null}
              </span>
              <span className="prise-curseur" style={{ left: `${curseur * 100}%` }} />
              {eternue !== "aucun" ? <span className="prise-barre-eternue" aria-hidden="true" /> : null}
            </div>
            <div className="prise-graduations" aria-hidden="true">
              {Array.from({ length: 11 }, (_, i) => (
                <span key={i} />
              ))}
            </div>
          </div>

          <button
            className="bouton primaire grand prise-rec"
            type="button"
            // Aucun `onClick` : l'appui se prend à `pointerdown` (plateau) et au clavier (fenêtre),
            // l'événement le plus tôt, jamais le « click » qui arrive au relâché. Un `onClick` ici
            // compterait l'appui deux fois.
            disabled={!enPrise}
            aria-label="REC : couper la prise (Espace, clic ou toucher)"
          >
            <span className="prise-rec-point" aria-hidden="true" />
            {eternue !== "aucun" ? "Non !" : "REC"}
          </button>
        </div>
      </div>
    </div>
  );
}
